import type { MouseEvent, ReactNode } from 'react'
import OrderSummary from './OrderSummary'
import { markInternalCheckoutBack } from '@/lib/ecommerce/checkoutStorage'

/*
  Shared horizontal stepper rendered above every checkout page.
  No Figma design yet — follows MIG body typography. All styling lives
  in src/styles/components/checkout-steps.css.
*/

export type CheckoutStep = 'contact' | 'shipping' | 'billing' | 'payment'

const STEPS: Array<{ key: CheckoutStep; label: string; href: string }> = [
  { key: 'contact', label: '1. Contact', href: '/checkout' },
  { key: 'shipping', label: '2. Shipping', href: '/checkout/shipping' },
  { key: 'billing', label: '3. Billing', href: '/checkout/billing' },
  { key: 'payment', label: '4. Payment', href: '/checkout/payment' },
]

export function CheckoutSteps({ active }: { active: CheckoutStep }) {
  const activeIndex = STEPS.findIndex((s) => s.key === active)

  return (
    <ol aria-label="Checkout progress" className="mig-checkout-steps">
      {STEPS.map((step, idx) => {
        const isActive = idx === activeIndex
        // Steps before the active one are clickable (back-navigation always
        // OK). Steps after the active one are inert — the buyer must
        // submit the current form to advance, otherwise they could land
        // on Payment without contact/shipping/billing data persisted.
        const isAhead = idx > activeIndex

        return (
          <li key={step.key} className="mig-checkout-step">
            {isAhead ? (
              <span
                className="mig-checkout-step-link"
                aria-disabled="true"
                data-ahead="true"
                title="Complete the previous step to continue"
              >
                {step.label}
              </span>
            ) : (
              <a
                href={step.href}
                className="mig-checkout-step-link"
                aria-current={isActive ? 'step' : undefined}
                data-active={isActive}
              >
                {step.label}
              </a>
            )}
          </li>
        )
      })}
    </ol>
  )
}

export function CheckoutPageShell({
  title,
  active,
  children,
  backHref,
}: {
  title: string
  active: CheckoutStep
  children: ReactNode
  backHref: string
}) {
  return (
    <section className="mig-checkout-section">
      <h1 className="mig-checkout-page-title">{title}</h1>
      <CheckoutSteps active={active} />

      {/* 2/3 main + 1/3 sticky order summary on desktop, stacked on
          mobile (the .mig-checkout-grid CSS in globals.css handles the
          breakpoints). */}
      <div className="mig-checkout-grid">
        <div className="mig-checkout-main">{children}</div>
        <OrderSummary />
      </div>

      <div className="mig-checkout-back-row">
        <a
          href={backHref}
          className="mig-checkout-back"
          onClick={(event: MouseEvent<HTMLAnchorElement>) => {
            // Mimic browser-back behaviour for in-flow BACK clicks:
            // set a sessionStorage flag the destination's state-init
            // reads alongside PerformanceNavigationTiming so the
            // typed-values draft is restored on arrival. Only fire
            // for plain single-click left-button navigations going
            // to a /checkout/* step — Cmd/Ctrl/Shift/middle-click
            // opens in a new tab and leaves the original tab on the
            // same page (setting the flag there would falsely
            // trigger a draft restore on the next unrelated visit),
            // and an in-page BACK out to /cart is an "exit the
            // flow" navigation that should NOT set the flag.
            if (
              event.button !== 0 ||
              event.metaKey ||
              event.ctrlKey ||
              event.shiftKey ||
              event.altKey
            ) {
              return
            }
            if (backHref === '/checkout' || backHref.startsWith('/checkout/')) {
              markInternalCheckoutBack()
            }
          }}
        >
          BACK
        </a>
        <div className="mig-divider-line" />
      </div>
    </section>
  )
}

export function CheckoutFieldRow({
  label,
  children,
  required,
}: {
  label: string
  children: ReactNode
  required?: boolean
}) {
  return (
    <div className="mig-checkout-field-row">
      <label className="mig-co-field-label">
        {label}
        {required && <span aria-hidden="true"> *</span>}
      </label>
      <div className="mig-co-field-input-slot">{children}</div>
    </div>
  )
}

/* Re-exported className constants so other checkout pages can apply the
   same input / submit-button styling without duplicating the style block.
   These map to CSS classes in src/styles/components/checkout-steps.css. */
export const CHECKOUT_INPUT_CLASS = 'mig-checkout-input'
export const CHECKOUT_SUBMIT_CLASS = 'mig-checkout-submit'

/* Legacy inline-style constants — kept temporarily for files that still
   import them (CheckoutBilling/Contact/Shipping). New code should apply
   the className constants above instead. Once those three files migrate
   off the inline objects, remove these. */
export const checkoutInputStyle: React.CSSProperties = {
  width: '100%',
  padding: 'var(--space-sm) 0',
  border: 'none',
  borderBottom: 'none',
  outline: 'none',
  background: 'transparent',
  fontFamily: "var(--font-body, 'Poppins', sans-serif)",
  fontSize: 'var(--font-h2-small)',
  fontWeight: 300,
  color: '#000000',
}

export const checkoutPrimaryButtonStyle: React.CSSProperties = {
  padding: 'var(--space-md) var(--space-3xl)',
  backgroundColor: '#000000',
  color: '#FFFFFF',
  border: 'none',
  borderRadius: 0,
  fontFamily: "var(--font-body, 'Poppins', sans-serif)",
  fontWeight: 300,
  fontSize: 'var(--font-h3-large)',
  textTransform: 'uppercase',
  cursor: 'pointer',
  lineHeight: 1,
}
